/**
 * IBKR reconciliation (owner only, Stage 1, read only): each imported IBKR trade and end-of-day position beside the
 * Sentinel paper record it matches, with its status under the P4 rules (matched, recorded difference, investigate,
 * blocking; an entry outside the approved time is an alert only), the open exceptions with an owner sign-off, and
 * where the data came from. Data: /v1/ibkr/reconciliation/page (backend/ibkr_recon.py). The rules are configuration
 * (IBKR_RECON_* variables) and are still proposed, pending Andrew's written confirmation.
 */
import { Fragment, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { QueryBoundary, retryTransient, apiError } from "@/lib/queryState";
import {
  Badge, Button, ConfirmModal, DataBanner, EmptyState, PageHead, Panel, RuleResult, Select, StatCell,
  etAndLocal, sessionDateLabel, withLocalTimes, dateTimeET, dayLabel, timeLocal, localZone, localZoneAbbr,
} from "@/sentinel";

const isNum = (v) => v !== null && v !== undefined && v !== "" && !Number.isNaN(Number(v));
const pts = (v) => (isNum(v) ? Number(v).toFixed(2) : "—");
const signed = (v, dp = 2) => (isNum(v) ? (Number(v) === 0 ? "0" : `${Number(v) > 0 ? "+" : "−"}${Math.abs(Number(v)).toFixed(dp)}`) : "—");
const cash = (v, sym) => `${Number(v) < 0 ? "−" : ""}${sym}${Math.abs(Number(v)).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** "2026-10-30 07:15 ET" and "Fri 30 Oct 22:15 AEDT": an instant in New York time, then the viewer's own. */
const instantET = (at) => (at ? dateTimeET(at, { seconds: false }) : "—");
const instantLocal = (at) => (at ? `${dayLabel(at, localZone())} ${timeLocal(at, { seconds: false })} ${localZoneAbbr(at)}` : "");

/** AUD first when the report gives an AUD base rate; the US$ figure beside it. */
function money(usd, fx, base) {
  if (!isNum(usd)) return "—";
  if (base === "AUD" && isNum(fx)) return `${cash(Number(usd) * Number(fx), "A$")} (${cash(usd, "US$")})`;
  return cash(usd, "US$");
}
/** The same on two lines: A$ first, US$ under it. */
function Money({ usd, fx, base, sign = false }) {
  if (!isNum(usd)) return "—";
  const plus = sign && Number(usd) > 0 ? "+" : "";
  if (base === "AUD" && isNum(fx)) return <>{plus}{cash(Number(usd) * Number(fx), "A$")}<div className="sn-faint sn-small">{plus}{cash(usd, "US$")}</div></>;
  return <>{plus}{cash(usd, "US$")}</>;
}

export const STATUS = {
  matched: { result: "PASS", label: "Matched" },
  recorded: { result: "NA", label: "Recorded difference" },
  investigate: { result: "UNCONFIRMED", label: "Investigate" },
  blocking: { result: "FAIL", label: "Blocking" },
  not_checked: { result: "NA", label: "Not checked" },
};
// Short labels for a single check or exception (the row status uses the long ones above).
const LEVEL = { ...STATUS, recorded: { result: "NA", label: "Recorded" }, alert: { result: "NA", label: "Alert only" } };

export function StatusMark({ status, alert, testId }) {
  const s = STATUS[status] || STATUS.not_checked;
  return (
    <span className="sn-row" style={{ gap: "var(--space-1)", flexWrap: "wrap" }} data-testid={testId}>
      <RuleResult result={s.result}>{s.label}</RuleResult>
      {alert ? <Badge icon="BellRing" title="Entry outside the approved time: an alert, not a block">Alert</Badge> : null}
    </span>
  );
}

const LevelMark = ({ level }) => <RuleResult result={(LEVEL[level] || LEVEL.matched).result}>{(LEVEL[level] || LEVEL.matched).label}</RuleResult>;
const sideName = (s) => (s === "put" ? "Bull put" : s === "call" ? "Bear call" : s || "—");
const RECORD = { paper_bullput: "Paper bull put", paper_d0: "Paper D0" };

function checkValue(c, which, ctx) {
  const v = c[which];
  if (v === undefined || v === null) return "—";
  if (c.time) return etAndLocal(v, { day: ctx.day });
  if (c.unit === "usd") return <Money usd={v} fx={ctx.fx} base={ctx.base} />;
  if (c.unit === "points" || c.unit === "index") return isNum(v) ? Number(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : String(v);
  return String(v);
}

function checkDiff(c, ctx) {
  if (!isNum(c.diff)) return "";
  if (Number(c.diff) === 0) return "0";
  if (c.unit === "minutes") return `${signed(c.diff, 0)} min`;
  if (c.unit === "usd") {
    const sgn = c.diff > 0 ? "+" : "−";
    const a = Math.abs(c.diff);
    return ctx.base === "AUD" && isNum(ctx.fx) ? `${sgn}${cash(a * ctx.fx, "A$")} (${sgn}${cash(a, "US$")})` : `${sgn}${cash(a, "US$")}`;
  }
  return `${signed(c.diff)} pts`;
}

function Checks({ row, day, base }) {
  const ctx = { day, fx: row.ibkr?.fx_to_base, base };
  return (
    <table className="sn-table" aria-label="Checks" style={{ margin: 0 }}>
      <thead><tr><th>Check</th><th>IBKR</th><th>Sentinel</th><th className="r">Difference</th><th>Result</th></tr></thead>
      <tbody>
        {row.checks.map((c) => (
          <tr key={c.field} data-testid={`check-${c.field}`}>
            <td>{c.label}{c.note ? <div className="sn-faint sn-small">{c.note}</div> : null}</td>
            <td className="sn-num">{checkValue(c, "ibkr", ctx)}</td>
            <td className="sn-num">{checkValue(c, "paper", ctx)}</td>
            <td className="num">{checkDiff(c, ctx)}</td>
            <td>{c.pending ? <RuleResult result="NA">Pending</RuleResult> : <LevelMark level={c.level} />}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function PaperCell({ p }) {
  if (!p) return <span className="sn-faint">No Sentinel record</span>;
  return (
    <>
      {RECORD[p.record] || p.record}: {sideName(p.side)} {p.short}/{p.long}
      <div className="sn-faint sn-small">credit {pts(p.credit)} · {p.status}{isNum(p.cboe_close) ? ` · Cboe close ${Number(p.cboe_close).toFixed(2)}` : ""}</div>
    </>
  );
}

function TradeRows({ rows, day, base }) {
  // every row with a difference opens on its checks; matched rows stay closed
  const [open, setOpen] = useState(() => new Set(rows.filter((r) => r.status !== "matched" && r.checks.length).map((r) => r.id)));
  const toggle = (id) => setOpen((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  if (!rows.length) return <p className="sn-small sn-muted" style={{ margin: 0 }}>No IBKR trades and no paper trades on this day.</p>;
  return (
    <div className="sn-table-wrap">
      <table className="sn-table" aria-label="IBKR trades against Sentinel's records">
        <thead><tr><th>Status</th><th>IBKR trade</th><th>Sentinel record</th><th className="r">Credit IBKR / paper</th><th className="r">IBKR net</th><th /></tr></thead>
        <tbody>
          {rows.map((r) => {
            const i = r.ibkr;
            return (
              <Fragment key={r.id}>
                <tr data-testid={`recon-row-${r.type}`}>
                  <td><StatusMark status={r.status} alert={r.alert} /></td>
                  <td>
                    {r.type === "paper_only" ? <span className="sn-faint">No IBKR trade</span>
                      : r.type === "other_order" ? <>Other SPX order {i.group} ({i.right}s)<div className="sn-faint sn-small">{i.entry_time ? etAndLocal(i.entry_time, { day }) : ""}</div></>
                        : <>{sideName(i.side)} {i.short}/{i.long} × {i.contracts}<div className="sn-faint sn-small">{etAndLocal(i.entry_time, { day })} · {i.root}{i.exit?.status ? ` · ${i.exit.status}${i.exit.worthless ? " worthless" : ""}` : ""}</div></>}
                  </td>
                  <td><PaperCell p={r.paper} />{r.note ? <div className="sn-small" style={{ whiteSpace: "normal" }}>{r.note}</div> : null}</td>
                  <td className="num">{i?.credit !== undefined ? pts(i.credit) : "—"} / {pts(r.paper?.credit)}</td>
                  <td className="num">{i?.net !== undefined ? <Money usd={i.net} fx={i.fx_to_base} base={base} /> : "—"}</td>
                  <td>{r.checks.length ? <Button size="sm" variant="ghost" icon={open.has(r.id) ? "ChevronDown" : "ChevronRight"} onClick={() => toggle(r.id)} aria-expanded={open.has(r.id)}>Checks</Button> : null}</td>
                </tr>
                {open.has(r.id) ? <tr><td colSpan={6} style={{ background: "var(--surface-sunken)" }}><Checks row={r} day={day} base={base} /></td></tr> : null}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function PositionRows({ rows, checked, reported }) {
  if (!reported) return <p className="sn-small sn-muted" style={{ margin: 0 }}>This day's report has no Open Positions section, so positions were not checked.</p>;
  if (!rows.length) return <p className="sn-small sn-muted" style={{ margin: 0 }} data-testid="recon-no-positions">No open positions at the end of the day{checked ? ", and none expected" : ""}.</p>;
  return (
    <div className="sn-table-wrap">
      <table className="sn-table" aria-label="End-of-day positions">
        <thead><tr><th>Status</th><th>Position</th><th className="r">Held</th><th className="r">Sentinel expects</th><th>Why</th></tr></thead>
        <tbody>
          {rows.map((r) => {
            const p = r.position || {};
            const what = p.kind === "spx_option" || p.right ? `${p.root || "SPXW"} ${p.strike} ${p.right || p.put_call} · expires ${p.expiry}` : `${p.symbol}${p.asset_class ? ` (${p.asset_class})` : ""}`;
            return (
              <tr key={r.id} data-testid="recon-row-position">
                <td><StatusMark status={r.status} /></td>
                <td>{what}{p.position_value !== undefined && p.position_value !== null ? <div className="sn-faint sn-small">value {cash(p.position_value, "US$")}</div> : null}</td>
                <td className="num">{signed(p.position, 0)}</td>
                <td className="num">{checked ? signed(p.expected ?? 0, 0) : "—"}</td>
                <td className="sn-small" style={{ whiteSpace: "normal" }}>{r.note || "—"}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Exceptions({ ex, onSignOff, matching }) {
  if (!matching) return <p className="sn-small sn-muted" style={{ margin: 0 }}>No exceptions while matching is off.</p>;
  return (
    <div className="sn-stack" style={{ gap: "var(--space-4)" }}>
      {ex.open.length ? (
        <div className="sn-table-wrap">
          <table className="sn-table" aria-label="Open exceptions">
            <thead><tr><th>Level</th><th>Day</th><th>What</th><th>First seen</th><th /></tr></thead>
            <tbody>
              {ex.open.map((f) => (
                <tr key={f.id} data-testid="exception-open">
                  <td><LevelMark level={f.level} /></td>
                  <td className="sn-num">{f.day}</td>
                  <td style={{ whiteSpace: "normal" }}>{withLocalTimes(f.detail, { day: f.day })}
                    {f.previous_review ? <div className="sn-faint sn-small">Changed since it was signed off {instantET(f.previous_review.reviewed_at)}</div> : null}</td>
                  <td className="sn-small">{f.first_seen_at ? <>{instantET(f.first_seen_at)}<div className="sn-faint">{instantLocal(f.first_seen_at)}</div></> : "—"}</td>
                  <td><Button size="sm" icon="Check" onClick={() => onSignOff(f)} data-testid="exception-signoff">Sign off</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : <p className="sn-small sn-muted" style={{ margin: 0 }} data-testid="exceptions-none">No open exceptions.</p>}
      {ex.reviewed.length ? (
        <div className="sn-stack" style={{ gap: "var(--space-2)" }}>
          <span className="sn-small sn-muted">Signed off ({ex.reviewed.length})</span>
          <div className="sn-table-wrap">
            <table className="sn-table" aria-label="Signed-off exceptions">
              <thead><tr><th>Level</th><th>Day</th><th>What</th><th>Signed off</th><th>Note</th></tr></thead>
              <tbody>
                {ex.reviewed.map((f) => (
                  <tr key={f.id} data-testid="exception-reviewed">
                    <td><LevelMark level={f.level} /></td>
                    <td className="sn-num">{f.day}</td>
                    <td style={{ whiteSpace: "normal" }}>{withLocalTimes(f.detail, { day: f.day })}</td>
                    <td className="sn-small">{f.review.reviewed_by_name} · {instantET(f.review.reviewed_at)}<div className="sn-faint">{instantLocal(f.review.reviewed_at)}</div></td>
                    <td className="sn-small" style={{ whiteSpace: "normal" }}>{f.review.note || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </div>
  );
}

const RULE_ROWS = [
  ["strikes", "Strike, leg or quantity mismatch; an unmatched IBKR order; an unexpected position", () => "always", "blocking"],
  ["credit", "Credit difference", (r) => `over ${pts(r.credit)} option points`, "investigate"],
  ["settlement", "Settlement against Cboe's official value", (r) => `over ${pts(r.settlement)} index points`, "investigate"],
  ["net_per_contract", "Net P&L a contract", (r) => `over US$${pts(r.net_per_contract)}`, "investigate"],
  ["entry_minutes", "Entry time", (r) => `over ${r.entry_minutes} minutes from ${r.approved_entry_time} ET`, "alert"],
  ["paper_without_ibkr_level", "Paper signal with no IBKR trade", () => "always", null],
  ["approved_contracts", "Approved size (when the paper record has none)", (r) => `${r.approved_contracts} contract(s)`, null],
  ["position_allowlist", "Positions allowed without a Sentinel spread", (r) => (r.position_allowlist?.length ? r.position_allowlist.join(", ") : "none"), null],
];

function Rules({ rep }) {
  const r = rep.rules;
  return (
    <div className="sn-stack" style={{ gap: "var(--space-3)" }}>
      <p className="sn-small sn-muted" style={{ margin: 0 }}>
        Decision P4, {rep.status}. Smaller differences are recorded, not raised. Each value is a setting on the server ({"IBKR_RECON_*"}); a change applies to this page at once.
      </p>
      {rep.errors?.length ? <DataBanner kind="stale" title="A RULE SETTING WAS IGNORED">{rep.errors.join(" ")}</DataBanner> : null}
      <div className="sn-table-wrap">
        <table className="sn-table" aria-label="Reconciliation rules">
          <thead><tr><th>Rule</th><th>Threshold</th><th>Result</th><th>Set by</th></tr></thead>
          <tbody>
            {RULE_ROWS.map(([key, label, thr, lvl]) => (
              <tr key={key}>
                <td style={{ whiteSpace: "normal" }}>{label}</td>
                <td className="sn-num">{thr(r)}</td>
                <td>{lvl || key === "paper_without_ibkr_level" ? <LevelMark level={lvl || r.paper_without_ibkr_level} /> : <span className="sn-faint">setting</span>}</td>
                <td className="sn-small sn-faint">{key === "strikes" ? "fixed" : rep.sources?.[key] === "default" ? `default (${rep.env?.[key]})` : rep.sources?.[key] || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SourcePanel({ src, snapshot }) {
  const li = src.last_import;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="recon-source">
      <StatCell size="md" label="Last import" value={li ? instantET(li.imported_at || li.fetched_at) : "None yet"}
        sub={li ? `${instantLocal(li.imported_at || li.fetched_at)} · ${li.trigger === "scheduled" ? "scheduled fetch" : li.trigger || "import"} · report for ${li.from_date === li.to_date ? li.to_date : `${li.from_date} to ${li.to_date}`}` : src.schedule} />
      <StatCell size="md" label="Data source" value={li?.synthetic ? "Synthetic fixture" : "IBKR Flex"} sub={`${src.data_source}${li?.query_name ? ` · query "${li.query_name}"` : ""}`} />
      <StatCell size="md" label="Account" value={src.account || "not set"} sub={`${src.reports_imported} report(s) imported${snapshot?.report_sha256 ? ` · this day's report ${snapshot.report_sha256.slice(0, 12)}` : ""}`} />
      <StatCell size="md" label="Field names" value={src.field_names_confirmed ? "Confirmed" : "Unconfirmed"} sub={src.field_names_confirmed ? "checked against a real report" : "check by hand on the first real report"} />
    </div>
  );
}

function Page({ data, day, setDay, onSignOff }) {
  const src = data.source;
  const li = src.last_import;
  if (data.empty) {
    return (
      <div className="sn-stack" style={{ gap: "var(--space-5)" }}>
        <EmptyState icon="FileText" title="No IBKR report yet" data-testid="recon-empty">
          {src.configured
            ? `The import is set up for ${src.account}. The first report arrives with the next fetch (${src.schedule}) or Fetch now; trades and positions show here once it is imported.`
            : `The import is off until ${src.missing_settings.join(", ")} are set on the server. No IBKR token has been created, and nothing is fetched until it is.`}
          {src.last_fetch && !src.last_fetch.ok ? ` The last fetch failed (${src.last_fetch.error_code || src.last_fetch.status}).` : ""}
        </EmptyState>
        <Panel title="Rules" icon="Scale"><Rules rep={data.rules} /></Panel>
      </div>
    );
  }
  const trades = data.rows.filter((r) => r.type !== "position");
  const positions = data.rows.filter((r) => r.type === "position");
  const c = data.counts;
  const ex = data.exceptions;
  const openN = ex.open.length;
  return (
    <div className="sn-stack" style={{ gap: "var(--space-5)" }}>
      {li?.synthetic ? <DataBanner kind="stub" title="SYNTHETIC TEST DATA" data-testid="recon-synthetic">These reports are synthetic test fixtures in IBKR's Flex shape, not Andrew's account. No IBKR token exists yet.</DataBanner> : null}
      {!src.matching_enabled ? <DataBanner kind="stale" title="MATCHING OFF">IBKR trades are shown without statuses until the first real report has been checked by hand and IBKR_MATCHING_ENABLED is switched on.</DataBanner> : null}
      <SourcePanel src={src} snapshot={data.snapshot} />
      <div className="sn-row" style={{ gap: "var(--space-3)", alignItems: "flex-end", flexWrap: "wrap" }}>
        <Select label="Trading day" value={day || data.day} onChange={(v) => setDay(v?.target ? v.target.value : v)} width={220}
          options={data.days.map((d) => ({ value: d, label: sessionDateLabel(d) }))} data-testid="recon-day" />
        <span className="sn-small sn-muted">Times are New York time with the local time beside them. Money is A$ first when the report gives the AUD rate.</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="recon-counts">
        <StatCell size="md" label="Matched" value={String(c.matched ?? 0)} />
        <StatCell size="md" label="Recorded difference" value={String(c.recorded ?? 0)} sub={c.alerts ? `${c.alerts} with an alert` : null} />
        <StatCell size="md" label="Investigate" value={String(c.investigate ?? 0)} />
        <StatCell size="md" label="Blocking" value={String(c.blocking ?? 0)} />
        <StatCell size="md" label="Open exceptions" value={String(openN)} sub={openN ? `all days: ${ex.open_counts.blocking || 0} blocking, ${ex.open_counts.investigate || 0} investigate, ${ex.open_counts.alert || 0} alert` : "all days"} />
      </div>
      <Panel title={`Trades · ${sessionDateLabel(data.day)}`} icon="ClipboardCheck" meta={<Badge>{trades.length} row(s)</Badge>}>
        <TradeRows key={data.day} rows={trades} day={data.day} base={data.base_currency} />
      </Panel>
      <Panel title="Positions at the end of the day" icon="Layers" meta={<Badge>{positions.length}</Badge>}>
        <PositionRows rows={positions} checked={data.positions_checked} reported={data.snapshot?.positions_reported} />
      </Panel>
      <Panel title="Exceptions" icon="TriangleAlert" meta={<Badge>{openN} open</Badge>} data-testid="recon-exceptions">
        <Exceptions ex={ex} onSignOff={onSignOff} matching={src.matching_enabled} />
      </Panel>
      <Panel title="Rules" icon="Scale"><Rules rep={data.rules} /></Panel>
    </div>
  );
}

export default function IbkrReconciliation() {
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const day = /^\d{4}-\d{2}-\d{2}$/.test(params.get("day") || "") ? params.get("day") : null;
  const setDay = (d) => setParams(d ? { day: d } : {}, { replace: true });
  const [signing, setSigning] = useState(null);
  const q = useQuery({ queryKey: ["ibkr-recon", day], queryFn: () => api.getIbkrReconciliationPage(day), retry: retryTransient, placeholderData: (prev) => prev });
  const signOff = async (note) => {
    try {
      await api.signOffIbkrException(signing.id, { day: signing.day, note: note || "" });
      toast.success("Exception signed off.");
      qc.invalidateQueries({ queryKey: ["ibkr-recon"] });
    } catch (e) { throw new Error(apiError(e)?.message || "Could not sign off"); }
  };
  return (
    <div className="sn-stack" style={{ gap: "var(--space-5)" }}>
      <PageHead crumb={["Evidence"]} title={<span data-testid="recon-title">IBKR reconciliation</span>}
        context="Imported IBKR trades and positions against Sentinel's paper records · read only · owner only" />
      <QueryBoundary query={q} loading={4} what="the IBKR reconciliation" errorTestId="recon-error" isEmpty={() => false}>
        {(data) => <Page data={data} day={day} setDay={setDay} onSignOff={setSigning} />}
      </QueryBoundary>
      {signing ? (
        <ConfirmModal title="Sign off this exception" confirmLabel="Sign off" icon="Check" onClose={() => setSigning(null)} onConfirm={signOff}
          field={{ label: "What was checked", multiline: true, required: signing.level === "blocking", minLength: signing.level === "blocking" ? 3 : undefined,
            hint: signing.level === "blocking" ? "Required for a blocking exception." : "Optional." }}>
          {sessionDateLabel(signing.day)} · {(LEVEL[signing.level] || {}).label}: {withLocalTimes(signing.detail, { day: signing.day })}. Signing off marks it reviewed; the difference stays on the record. If it changes later it opens again.
        </ConfirmModal>
      ) : null}
    </div>
  );
}
